<template>
    <DCard padding>
        <DRow
            class="mb-3"
            align-vertical="end"
        >
            <DCol
                sm="12"
            >
                <DButton
                    id="table-configuration"
                    icon="cog-outline"
                    :label="__('messages.list_configuration')"
                    class="float-end me-3 mt-3"
                    @click="showListViewModal = true"
                />
            </DCol>
        </DRow>
        <DRow
            class="mt-2 mb-2"
            align-vertical="end"
        >
            <DCol
                sm="2"
                class="pt-1"
            >
                <DDateFormatInput
                    id="date_from"
                    v-model="filterData.dateFrom"
                    format="YYYY-MM-DD"
                    :label="__('customers.business_incidents.date_from')"
                    class="mb-0"
                    type="date"
                    @keydown.enter.exact.prevent="changePage(businessIncidents.current_page)"
                />
            </DCol>
            <DCol
                sm="2"
                class="pt-1"
            >
                <DDateFormatInput
                    id="date_to"
                    v-model="filterData.dateTo"
                    format="YYYY-MM-DD"
                    :label="__('customers.business_incidents.date_to')"
                    class="mb-0"
                    type="date"
                    @keydown.enter.exact.prevent="changePage(businessIncidents.current_page)"
                />
            </DCol>
            <DCol
                sm="2"
                class="pt-1"
            >
                <DSelect
                    id="type"
                    v-model="filterData.status"
                    :label="__('customers.business_incidents.status')"
                    :options="statusOptions"
                />
            </DCol>
            <DCol
                sm="2"
                class="pt-1"
            >
                <DInput
                    id="subject_id"
                    v-model="filterData.subjectId"
                    :label="__('customers.business_incidents.subject_id')"
                />
            </DCol>
            <DCol
                sm="2"
                class="pt-1"
            >
                <DMultiSelect
                    id="incident_type"
                    :label="__('customers.business_incidents.incident_type')"
                    :options="incidentTypesOptions"
                    :selection="true"
                    :search="true"
                    selection-type="text"
                    options-empty-placeholder="No options placeholder"
                    @update="updateIncidentTypes"
                />
            </DCol>
            <DCol
                sm="2"
                class="pt-1"
            >
                <DSelect
                    id="type"
                    v-model="filterData.updateUser"
                    :label="__('customers.business_incidents.update_user')"
                    :options="users"
                />
            </DCol>
        </DRow>
        <DRow
            class="mt-2 mb-2"
            align-vertical="end"
        >
            <DCol
                sm="3"
                class="d-flex gap-2"
            >
                <DButton
                    :label="__('messages.actions.filter')"
                    @click="changePage(businessIncidents.current_page)"
                />
                <DButton
                    icon="filter-off"
                    :label="__('messages.actions.reset_filter')"
                    @click="resetFilters"
                />
            </DCol>
        </DRow>
        <DTable
            :items="items"
            :fields="getFields"
            class="table-responsive"
            :sorter="isSortable"
            :sorter-value="sorterValue"
            @update:sorter-value="updateIncidents"
        >
            <template #id="{item}">
                <td>
                    <Link :href="incidentRoute(item)">
                        {{ item.id }}
                    </Link>
                </td>
            </template>
            <template #date="{item}">
                <td>
                    {{ item.date }}
                </td>
            </template>
            <template #type="{item}">
                <td>
                    {{ item.type ?? '' }}
                </td>
            </template>
            <template #customer_request="{item}">
                <td>
                    {{ item.customer_request ?? '' }}
                </td>
            </template>
            <template #incident_solution="{item}">
                <td>
                    {{ item.incident_solution ?? '' }}
                </td>
            </template>
            <template #contracts="{item}">
                <td>
                    <div
                        v-for="(id, index) in item.contracts"
                        :key="index"
                    >
                        <Link
                            v-if="userPermissions.includes('contract.view')"
                            :href="route('customers.customers.contracts.show', {customer: customer.id, contract: id})"
                        >
                            {{ id + (index < item.contracts.length - 1 ? ',' : ' ') }}
                        </Link>
                        <span
                            v-else
                        >{{ id + (index < item.contracts.length - 1 ? ',' : ' ') }}</span>
                    </div>
                </td>
            </template>
            <template #products="{item}">
                <td>
                    <div
                        v-for="(id, index) in item.products"
                        :key="index"
                    >
                        <Link
                            v-if="userPermissions.includes('product.view')"
                            :href="route('products.products.show', {product: id})"
                        >
                            {{ id + (index < item.contracts.length - 1 ? ',' : ' ') }}
                        </Link>
                        <span
                            v-else
                        >{{ id + (index < item.contracts.length - 1 ? ',' : ' ') }}</span>
                    </div>
                </td>
            </template>
            <template #status="{item}">
                <td>
                    {{ __('customers.business_incidents.statuses.' + item.status) }}
                </td>
            </template>
        </DTable>
        <DPagination
            v-if="businessIncidents.last_page > 1"
            :active-page="businessIncidents.current_page"
            :pages="businessIncidents.last_page"
            align="center"
            @update:activePage="changePage"
        />

        <DFabs>
            <DFab
                v-if="userPermissions.includes('customer.incidents_manage')"
                id="btn-add"
                icon="plus"
                :label="__('customers.business_incidents.open_incident')"
                @click="openIncident"
            />
        </DFabs>
        <ListViewModal
            v-if="showListViewModal"
            :show-modal="showListViewModal"
            :user-list-view="listView"
            @close-modal="showListViewModal = false"
        />
    </DCard>
</template>

<script>
import App from '~@/App.vue'
import { router, usePage } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import CustomerBase from '~@/Pages/Customers/Base'
import { computed, ref } from 'vue'
import { __ } from '~@/Composables/translation'
import ListViewModal from '~@/Components/ListView/ListViewModal'
export default {
    layout: [App, CustomerBase]
}
</script>

<script setup>
const props = defineProps({
    businessIncidents: {
        type: Object,
        default: () => {
            return {}
        }
    },
    items: {
        type: Object,
        default: () => {
            return {}
        }
    },
    statuses: {
        type: Array,
        default: () => {
            return []
        }
    },
    filters: {
        type: Object,
        default: () => {
            return {}
        }
    },
    customer: {
        type: Object,
        default: () => {
            return {}
        }
    },
    listView: {
        type: Array,
        default: () => {
            return []
        }
    },
    incidentTypes: {
        type: Array,
        default: () => {
            return []
        }
    },
    users: {
        type: Array,
        default: () => {
            return []
        }
    }
})

const filterData = ref({
    dateFrom: props.filters.dateFrom ?? null,
    dateTo: props.filters.dateTo ?? null,
    status: props.filters.status ?? null,
    order_by: props.filters.order_by,
    order_by_asc: props.filters.order_by_asc ? 1 : 0,
    subjectId: props.filters.subjectId ?? null,
    incidentType: props.filters.incidentType ?? [],
    updateUser: props.filters.updateUser ?? null
})

const showListViewModal = ref(false)
const openIncident = () => {
    router.get(route('customers.customers.business-incidents.create', { customer: props.customer.id }))
}

const statusOptions = computed(function () {
    const statuses = props.statuses.map(status => {
        return {
            value: status.value,
            label: status.text
        }
    })

    statuses.unshift({
        value: '',
        label: ''
    })

    return statuses
})

const incidentTypesOptions = computed(function () {
    return props.incidentTypes.map((item) => {
        // Ensure filterData.value.incidentType is an array
        const incidentType = Array.isArray(filterData?.value?.incidentType) ? filterData.value.incidentType : []

        // Ensure both values are strings for comparison
        const isSelected = incidentType.includes(String(item.value))
        return {
            value: item.value,
            text: item.text,
            selected: isSelected // Mark selected if matched
        }
    })
})

const getFields = computed(() => {
    const availableList = []

    props.listView.forEach((item) => {
        if (item.show_column === true) {
            availableList.push({
                key: item.column,
                label: item.column !== 'actions' && item.column !== 'icon' ? __('settings.list_view.' + item.list + '.' + item.column) : '',
                sorter: item.sort_column_enabled,
                _style: item.width !== null ? { minWidth: item.width + 'px', maxWidth: item.width + 'px' } : {}
            })
        }
    })

    return availableList
})

const userPermissions = computed(() => usePage().props.auth.permissions)

const changePage = (page) => {
    const query = Object.fromEntries(Object.entries(filterData.value).filter(([_, v]) => v))
    if (page > 1) {
        query.page = page
    }

    router.get(route('customers.customers.business-incidents.index', { customer: props.customer.id }), query)
}

const sorterValue = computed(function () {
    return {
        column: filterData.value.order_by,
        asc: !!props.filters.order_by_asc
    }
})

const isSortable = computed(() => {
    if (!sorterValue.value.column) return false

    return { external: true }
})

const resetFilters = () => {
    filterData.value.dateFrom = null
    filterData.value.dateTo = null
    filterData.value.status = null
    filterData.value.relationId = null
    filterData.value.relationType = null
    filterData.value.subjectId = null
    filterData.value.incidentType = null
    filterData.value.updateUser = null

    router.get(route('customers.customers.business-incidents.index', { customer: props.customer.id }))
}

function incidentRoute (item) {
    return route('customers.customers.business-incidents.edit', { customer: props.customer.id, business_incident: item.id })
}

const updateIncidentTypes = (values) => {
    filterData.value.incidentType = values
}

const updateIncidents = (event) => {
    filterData.value.order_by = event.column
    filterData.value.order_by_asc = event.asc ? 1 : 0

    changePage(props.businessIncidents.current_page)
}

</script>

<style lang="scss" scoped>
.ul-class{
    margin: 0;
    padding-left: 15px;
}
</style>
